import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Dialog } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileText } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionOrderDocumentClient, type ProductionOrderDocumentDTO } from "../../api/cutJobs";
import { PartFileClient, type PartFileDTO } from "../../api/manufacturing";

/** A document a station can show: its name, and how to get a short-lived link to it. */
export interface StationDocument {
  key: string;
  name: string;
  link: () => Promise<string>;
}

/**
 * A station's document button (View part, View cut plan, View assembly plan):
 * greyed out when there is nothing to show, else a preview inside the station,
 * a PDF or an image in a frame. Several documents (a job's Cut Rite plans)
 * are switched at the top of the preview.
 */
export function ViewDocumentButton({ label, documents, loading }: { label: string; documents: StationDocument[]; loading?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const none = documents.length === 0;
  return (
    <>
      <button
        type="button"
        disabled={none || loading}
        onClick={() => setOpen(true)}
        title={none && !loading ? t("kiosk.document.none", "No document to show") : undefined}
        className="flex min-h-12 items-center gap-2 rounded-lg border border-line bg-surface px-4 text-[16px] font-semibold hover:bg-surface-sunk disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FileText className="size-5" />
        {label}
      </button>
      {open && <PreviewDialog title={label} documents={documents} onClose={() => setOpen(false)} />}
    </>
  );
}

const SHOWN = /\.(pdf|png|jpe?g|gif|webp|svg)$/i;

function PreviewDialog({ title, documents, onClose }: { title: string; documents: StationDocument[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(documents[0]!);
  const link = useQuery({ queryKey: ["station-document", current.key], queryFn: current.link, staleTime: 60_000 });
  const image = /\.(png|jpe?g|gif|webp|svg)$/i.test(current.name);
  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={title}
      description={current.name}
      actions={
        <>
          {link.data && (
            <a href={link.data} target="_blank" rel="noreferrer" className="mr-auto inline-flex items-center gap-1 text-[14px] text-accent underline">
              <ExternalLink className="size-4" />
              {t("kiosk.document.open", "Open in a new tab")}
            </a>
          )}
          <Button variant="primary" onClick={onClose}>{t("dialog.close", "Close")}</Button>
        </>
      }
    >
      {documents.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {documents.map((document) => (
            <button
              key={document.key}
              type="button"
              onClick={() => setCurrent(document)}
              className={cn("rounded-lg border px-3 py-2 text-[14px]", document.key === current.key ? "border-accent bg-accent-soft font-semibold" : "border-line")}
            >
              {document.name}
            </button>
          ))}
        </div>
      )}
      <div className="h-[72vh] overflow-hidden rounded-md border border-line bg-surface-sunk">
        {link.isLoading && <p className="p-6 text-[16px] text-ink-muted">{t("table.loading", "Loading")}</p>}
        {link.isError && <p className="p-6 text-[16px] text-risk">{t("kiosk.document.failed", "The document could not be opened.")}</p>}
        {link.data &&
          (!SHOWN.test(current.name) ? (
            <p className="p-6 text-[16px] text-ink-muted">{t("kiosk.document.not-shown", "This kind of file is not shown here: open it in a new tab.")}</p>
          ) : image ? (
            <img src={link.data} alt={current.name} className="h-full w-full object-contain" />
          ) : (
            <iframe src={link.data} title={current.name} className="h-full w-full" />
          ))}
      </div>
    </Dialog>
  );
}

/** The current files of a part (its drawing) or of a product (its exploded view, its guide), of the kinds asked, in that order. */
export function usePartFiles(owner: { partId?: number | null; productId?: number | null }, kinds: PartFileDTO["kind"][]) {
  const { http } = useAuth();
  const client = useMemo(() => new PartFileClient(http), [http]);
  const filter: Record<string, string[]> | null =
    owner.partId != null ? { part_id: [String(owner.partId)] } : owner.productId != null ? { product_id: [String(owner.productId)] } : null;
  const files = useQuery({
    queryKey: ["manufacturing", "part-file", "station", owner.partId ?? null, owner.productId ?? null, kinds],
    queryFn: async () =>
      (await client.find({ page: 0, size: 50, filters: { ...filter!, is_current: ["true"], kind: kinds } })).content
        .filter((file) => file.is_current)
        .sort((a, b) => kinds.indexOf(a.kind) - kinds.indexOf(b.kind)),
    enabled: filter != null,
    staleTime: 60_000,
  });
  const documents: StationDocument[] = (files.data ?? []).map((file) => ({
    key: `part-file:${String(file.id)}`,
    name: file.file_name,
    link: () => client.link(file.id),
  }));
  return { documents, loading: files.isLoading && filter != null };
}

/** A cut job's documents (its Cut Rite plans first), as the station shows them. */
export function useCutJobDocuments(documents: ProductionOrderDocumentDTO[]): StationDocument[] {
  const { http } = useAuth();
  const client = useMemo(() => new ProductionOrderDocumentClient(http), [http]);
  return [...documents]
    .sort((a, b) => Number(a.kind === "OTHER") - Number(b.kind === "OTHER"))
    .map((document) => ({ key: `production-document:${String(document.id)}`, name: document.file_name, link: () => client.link(document.id) }));
}
